import {useMemo} from 'react';
import {parseActions} from '@netx/core-next/utils';
import type {ReactAction} from '@netx/core/bootstrap';
import {useCategory} from '@netx/core-category/hooks/useCategory';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {
	categoryActionAdd,
	categoryActionCreatePDF,
	categoryActionDelete,
	categoryActionDownload,
	categoryActionEdit,
	categoryActionImport,
	categoryActionSearchAllAssets,
	categoryActionShareMenu,
} from '../actions';
import {type ICategory} from '../schema';

export type UseCategoryActionsArguments = {
	id?: number;
	hideExtendedActions?: boolean;
	order?: boolean;
};

export const useCategoryActions = ({
	id,
	...props
}: UseCategoryActionsArguments = {}) => {
	const {category, loading} = useCategory({id});

	const getActions = useConstCallback(
		({
			category,
			hideExtendedActions,
			order = true,
		}: Omit<UseCategoryActionsArguments, 'id'> & {
			category?: ICategory;
		} = {}) => {
			if (!category) return [];

			const actions: ReactAction[] = [
				categoryActionAdd(category),
				categoryActionDownload(category),
				categoryActionCreatePDF(category),
				categoryActionShareMenu(category),
				categoryActionImport(category),
			];

			if (!hideExtendedActions) {
				actions.push(
					categoryActionSearchAllAssets(category),
					categoryActionEdit(category),
					categoryActionDelete(category, {group: 1}),
				);
			}

			return parseActions({
				actions,
				args: [category],
				order,
			});
		},
	);

	const actions = useMemo(
		() => getActions({category, ...props}),
		[category, props],
	);

	return {
		actions,
		getActions,
		loading,
	};
};

export default useCategoryActions;
